import className from 'classnames';
import * as dateFnsCompat from 'utils/date-fns-compat';
import fp from 'lodash/fp';
import React from 'react';
import { FiCheck } from 'react-icons/fi';
import { connect } from 'react-redux';
import ScaleText from "react-scale-text";
import Spinner from 'react-spinkit';
import { compose, withHandlers, withProps } from 'recompose';
import { abbreviatedMonths } from '../../../../../constants/months';
import { isTotalBased } from '../../../../../services';
import { noCapMap } from '../../../../../utils';
import { Icon, SelectFormField, ToggleBox } from '../../../../components';
import { AddPointIcon, MinusPointIcon } from '../partials';
import { currentUserIndustry, currentUserSelector } from './../../../../../state/modules/user';
import { cpdCategoryType } from '../../../../../constants';

const mapStateToProps = (state) => ({
  user: currentUserSelector(state),
  industry: fp.first(currentUserIndustry(state)),
  industriesWithGoals: state.industry.industriesWithCpdCategorys,
  isTotalBased: isTotalBased(currentUserIndustry(state))
})
const mapDispatchToProps = (dispatch) => ({})

const EntityTrainingPlan = ({
  entity,
  industryEntity,
  showTrainingPlanRequirements,
  trainingPlan,
  isEditing,
  isSaved,
  hasFailed,
  hideFailedBadge,
  cpdCategorys,
  onSelectCpdCategory,
  toggleEditing,
  batchToggleCpdCategorys,
  addCpdPoints,
  minusCpdPoints,
  saveEntityTrainingPlan,
  isUpdating,
  isTotalBased,
  locked,
  multiple,
  updateCpdDate,
  updateCpdMonth,
  addCpdRequirement,
  minusCpdRequirement,
  goals,
  industryGoals,
  addProgressGoalPoints,
  minusProgressGoalPoints,
  showBreakdown,
  setShowSaveErrorModal,
  setSaveError,
}) => {
  return <div id='entity-training-plan-wrapper'>
    <div className='placeholder'>
      <div className='shadow-blocker'></div>
    </div>
    <div id='entity-training-plan' className={className({ isEditing, narrow: multiple })}>
      {
        fp.flow(
          (cpdCategorys) => {
            const coreCpdCategories = cpdCategorys.filter(({ parent }) => parent.categoryType === cpdCategoryType.core);
            const coreCpdCategoryIds = coreCpdCategories.map(({ parent }) => parent.id);
            const subCpdCategories = cpdCategorys.filter(({ parent }) => parent.categoryType === cpdCategoryType.sub);
            const subCpdCategoryIds = subCpdCategories.map(({ parent }) => parent.id);

            const getCategoryPlans = fp.map(
              ({ parent, children, showChildren }) => {
                const { id, mandatory } = parent
                const trainingPlanEntry = fp.find(
                  ({ cpdCategoryId }) => cpdCategoryId === id
                )(trainingPlan)
                const label = trainingPlanEntry && trainingPlanEntry.totalCpdPoints >= 0 ? <span>{trainingPlanEntry.totalCpdPoints}</span> : '-'
                const hasChildren = children.length > 0
                const childrenSelected = fp.flow(
                  fp.filter(
                    (child) => fp.find(
                      ({ cpdCategoryId }) => cpdCategoryId === child.id
                    )(trainingPlan)
                  )
                )(children)
                const childrenUnselected = fp.flow(
                  fp.filter(
                    (child) => !fp.find(
                      ({ cpdCategoryId }) => cpdCategoryId === child.id
                    )(trainingPlan)
                  )
                )(children)
                const anyChildrenSelected = childrenSelected.length > 0
                const allChildrenSelected = childrenSelected.length === children.length
                const toggleChildrenLable = allChildrenSelected ?
                  <span id='tick'><FiCheck /></span> :
                  anyChildrenSelected ?
                    '-' :
                    null
                const toggleChildrenText = <div id='toggle-children-text' className={className({ visible: isEditing })}>
                  {
                    allChildrenSelected ?
                      'All categories will appear in the library' :
                      anyChildrenSelected ?
                        'The following categories will appear in the library' :
                        'All categories will appear in the library'
                  }
                </div>
                const childrenIds = fp.flow(
                  fp.flatMap('children'),
                  fp.map('id')
                )(cpdCategorys)

                // Calculate core or sub categories' total points
                const isCore = coreCpdCategoryIds.includes(id);
                const targetCpdCategoryIds = isCore ? coreCpdCategoryIds : subCpdCategoryIds;
                const totalPoints = fp.flow(
                  fp.filter(({ cpdCategoryId }) => targetCpdCategoryIds.includes(cpdCategoryId)),
                  fp.sumBy('totalCpdPoints'),
                )(trainingPlan);

                const childrenTotalPoints = fp.flow(
                  fp.filter(
                    ({ cpdCategoryId }) => fp.contains(cpdCategoryId)(childrenIds)
                  ),
                  fp.map('totalCpdPoints'),
                  fp.sum
                )(trainingPlan)
                const minusDisabled = (mandatory && trainingPlanEntry) 
                  ? hasChildren 
                      ? (trainingPlanEntry.totalCpdPoints <= childrenTotalPoints)
                      : (trainingPlanEntry.totalCpdPoints <= 0)
                  : (!trainingPlanEntry || trainingPlanEntry.totalCpdPoints < 0)
                const plusDisabled = (totalPoints - childrenTotalPoints) >= industryEntity.cpdPoints
  
                return <div key={id} className={className('cpd-category-group', { hasChildren, showChildren })}>
                  {
                    hasChildren ?
                      <div className='entity-cpd-category cell flexbox vertical'>
                        <div className='flexbox fill-space center align-center'>
                          <MinusPointIcon show={isEditing} disabled={minusDisabled} handler={() => minusCpdPoints(parent)} />
                          <ToggleBox
                            value={id}
                            label={label}
                            checked={!fp.isEmpty(trainingPlanEntry)} />
                          <AddPointIcon show={isEditing} disabled={plusDisabled} handler={() => addCpdPoints(parent)} />
                        </div>
                        <div id='select-all-children-box' className={className('flexbox center align-center', { visible: isEditing && showChildren && trainingPlanEntry })}>
                          <ToggleBox
                            small={true}
                            label={toggleChildrenLable}
                            checked={allChildrenSelected}
                            onChange={() => {
                              const deselectAllChildren = () => {
                                batchToggleCpdCategorys(childrenSelected)
                              }
                              const selectAllChildren = () => {
                                batchToggleCpdCategorys(childrenUnselected)
                              }
                              if (anyChildrenSelected) {
                                deselectAllChildren()
                              } else {
                                selectAllChildren()
                              }
                            }} />
                          <span className='select-all-text'>Select all</span>
                        </div>
                        {
                          toggleChildrenText
                        }
                      </div> :
                      <div className='entity-cpd-category cell flexbox center align-center'>
                        <MinusPointIcon show={isEditing} disabled={minusDisabled} handler={() => minusCpdPoints(parent)} />
                        <ToggleBox
                          value={id}
                          label={label}
                          checked={!fp.isEmpty(trainingPlanEntry)} />
                        <AddPointIcon show={isEditing} disabled={plusDisabled} handler={() => addCpdPoints(parent)} />
                      </div>
                  }
                  {
                    showChildren ? fp.map(
                      (child) => {
                        const parentTotal = fp.flow(
                          fp.find(
                            ({ cpdCategoryId }) => cpdCategoryId === parent.id
                          ),
                          fp.get('totalCpdPoints')
                        )(trainingPlan)
                        const childrenTotal = fp.flow(
                          fp.filter(
                            ({ cpdCategoryId }) => fp.contains(cpdCategoryId)(
                              fp.map('id')(children)
                            )
                          ),
                          fp.map('totalCpdPoints'),
                          fp.sum
                        )(trainingPlan)
                        const trainingPlanEntry = fp.find(
                          ({ cpdCategoryId }) => cpdCategoryId === child.id
                        )(trainingPlan)
                        const label = trainingPlanEntry ?
                          trainingPlanEntry.totalCpdPoints ?
                            trainingPlanEntry.totalCpdPoints :
                            <span id='tick'><FiCheck /></span> :
                          null
                        const minusDisabled = trainingPlanEntry ?
                          (trainingPlanEntry.totalCpdPoints <= 0) :
                          !trainingPlanEntry
                        const plusDisabled = childrenTotal >= parentTotal
                        return <div key={child.id} className='entity-cpd-category cell child flexbox center align-center'>
                          <MinusPointIcon show={isEditing} disabled={minusDisabled} handler={() => minusCpdPoints(child)} />
                          <ToggleBox
                            small={true}
                            value={child.id}
                            label={label}
                            checked={!fp.isEmpty(trainingPlanEntry)}
                            onChange={isEditing ? () => onSelectCpdCategory(child) : null} />
                          <AddPointIcon show={isEditing} disabled={plusDisabled} handler={() => addCpdPoints(child)} />
                        </div>
                      }
                    )(children) : null
                  }
                </div>
              }
            );

            return {
              coreCategoryPlanCells: getCategoryPlans(coreCpdCategories),
              subCategoryPlanCells: getCategoryPlans(subCpdCategories),
            };
          },
          ({ coreCategoryPlanCells, subCategoryPlanCells }) => {
            const industryGoalCells = fp.map(
              ({ id, name }) => {
                const points = fp.flow(
                  fp.find(
                    ({ goalId }) => goalId === id
                  ),
                  fp.getOr(0)('points')
                )(goals)
                return <div key={`industry-goal-${id}`} className='entity-industry-goal cell flexbox center align-center'>
                  {
                    isEditing ?
                      <div id='progress-goal-text' className='flexbox center align-center'>
                        <MinusPointIcon show={isEditing} disabled={false} handler={() => minusProgressGoalPoints(id)} />
                        {`${points}`}
                        <AddPointIcon show={isEditing} disabled={false} handler={() => addProgressGoalPoints(id)} />
                      </div> :
                      <div id='progress-goal-text'>{`${points}`}</div>
                  }
                </div>
              }
            )(industryGoals)
            return {
              coreCategoryPlanCells: showBreakdown ? coreCategoryPlanCells : [],
              subCategoryPlanCells: showBreakdown ? subCategoryPlanCells : [],
              industryGoalCells
            }
          },
          ({ coreCategoryPlanCells, subCategoryPlanCells, industryGoalCells }) => {
            const coreCpdCategories = cpdCategorys.filter(({ parent }) => parent.categoryType === cpdCategoryType.core);
            const coreCpdCategoryIds = coreCpdCategories.map(({ parent }) => parent.id);

            // Get points for valid core cpd categories.
            const pointsAllocated = fp.flow(
              fp.filter(
                ({ cpdCategoryId }) => {
                  return fp.find(
                    ({ parent }) => parent.id === cpdCategoryId && coreCpdCategoryIds.includes(parent.id)
                  )(cpdCategorys)
                }
              ),
              fp.sumBy('totalCpdPoints')
            )(trainingPlan)
            const requiredPoints = industryEntity.cpdPoints
            const shortage = requiredPoints - pointsAllocated
            const totalNotMet = (isTotalBased && (shortage !== 0)) || (shortage < 0)

            const onSave = () => {
              saveEntityTrainingPlan(entity.id)
            }

            const onTotalNotMetSave = () => {
              const saveRequirement = isTotalBased
                ? 'equal to the sum of core category hours'
                : 'greater than or equal to the sum of core category hours';
              const newSaveError = `Total progress hours should be ${saveRequirement}. Please update your training plan.`;
              setSaveError(newSaveError);
              setShowSaveErrorModal(true);
            }

            const totalCpdPlanCell = cpdCategorys.length > 1 ? [
              <div
                key='default-progress-goal-edit'
                className='entity-industry-goal cell flexbox center align-center'
                style={{ color: totalNotMet ? 'red' : undefined }}
              >
                {
                  isEditing ?
                    <div id='requirement-edit' className='flexbox justify-around align-center'>
                      <MinusPointIcon show={isEditing} disabled={industryEntity.cpdPoints <= 0} handler={minusCpdRequirement} />
                        {industryEntity.cpdPoints}
                      <AddPointIcon show={isEditing} disabled={false} handler={addCpdRequirement} />
                    </div> :
                    <div id='requirement-text'>{`${industryEntity.cpdPoints}`}</div>
                }
              </div>
            ] : [];

            const coreCategoryTitlePlanCell = coreCategoryPlanCells.length > 0 ? [
              <div
                key='core-category-edit'
                className='entity-industry-goal cell flexbox center align-center'
                style={{ background: 'lightgrey' }}
              />
            ] : [];

            const subCategoryTitlePlanCell = subCategoryPlanCells.length > 0 ? [
              <div
                key='sub-category-edit'
                className='entity-industry-goal cell flexbox center align-center'
                style={{ background: 'lightgrey' }}
              />
            ] : [];

            return [
              <div key='heading' className='entity-cpd-category heading cell flexbox align-center vertical nowrap'>
                {
                  locked ?
                    <span className={className('entity-cpd-category-toggle-button flexbox center align-center disabled', { locked })}>
                      LOCKED
                    </span> :
                    isEditing ?
                      totalNotMet ?
                        <span onClick={onTotalNotMetSave} className={className('entity-cpd-category-toggle-button flexbox center align-center disabled', { isEditing })}>
                          SAVE
                        </span> :
                        <span onClick={onSave} className={className('entity-cpd-category-toggle-button flexbox center align-center clickable', { isEditing })}>
                          SAVE
                      </span> :
                      isUpdating ?
                        <span className={className('entity-cpd-category-toggle-button flexbox center align-center updating')}>
                          <Spinner name='circle' className='spinner' fadeIn='none' />
                        </span> :
                        isSaved ?
                          <span className={className('entity-cpd-category-toggle-button flexbox center align-center', { isSaved })}>
                            Saved
                          </span> :
                          hasFailed ?
                            <span onClick={() => { hideFailedBadge(entity.id); toggleEditing(entity.id) }} className={className('entity-cpd-category-toggle-button flexbox center align-center clickable', { hasFailed })}>
                              Failed <Icon icon='edit-large' />
                            </span> :
                            <span onClick={() => toggleEditing(entity.id)} className={className('entity-cpd-category-toggle-button flexbox center align-center clickable')}>
                              <Icon icon='edit-large' />
                            </span>

                }
                {
                  showTrainingPlanRequirements ?
                    [
                      <div key='entity-name-label' id='entity-name-label' className={className({ isEditing })}>
                        <ScaleText minFontSize={11} maxFontSize={16}>
                          {entity.name}
                        </ScaleText>
                      </div>,
                      <div key='cpd-date-label' id='entity-cpd-date-label'>
                        {
                          isEditing ?
                            <div className='flexbox justify-between date-edit'>
                              <div className='select-month'>
                                <SelectFormField
                                  label={null}
                                  onChange={updateCpdMonth}
                                  emptyOption={false}
                                  required
                                  value={industryEntity.cpdMonth}>
                                  {
                                    fp.flow(
                                      fp.values,
                                      noCapMap((monthName, index) =>
                                        <option key={index} value={index + 1}>
                                          {monthName}
                                        </option>
                                      )
                                    )(abbreviatedMonths)
                                  }
                                </SelectFormField>
                              </div>
                              <div className='select-day'>
                                <SelectFormField
                                  label={null}
                                  onChange={updateCpdDate}
                                  emptyOption={false}
                                  required
                                  value={industryEntity.cpdDay}>
                                  {
                                    fp.map(
                                      (day) => <option key={day} value={day}>
                                        {day}
                                      </option>
                                    )(fp.range(1)(dateFnsCompat.getDaysInMonth(industryEntity.cpdMonth.toString()) + 1))
                                  }
                                </SelectFormField>
                              </div>
                            </div> :
                            <div id='date-text'>{`${fp.values(abbreviatedMonths)[industryEntity.cpdMonth - 1]} ${industryEntity.cpdDay}`}</div>
                        }
                      </div>
                    ] :
                    industryGoals.length > 0 ? [] : [
                      <div key='entity-cpd-category-heading' className='entity-cpd-category-heading'>Total points allocated</div>,
                      <div key='entity-cpd-category-points' className='flexbox center align-center fill-space'>
                        <div id='cpd-point-label'>{pointsAllocated}</div>
                        {
                          totalNotMet ?
                            <div id='shortage-label'>{`Add ${shortage} hours`}</div> :
                            null
                        }
                      </div>
                    ]
                }
              </div>
            ].concat(totalCpdPlanCell)
            .concat(coreCategoryTitlePlanCell).concat(coreCategoryPlanCells)
            .concat(subCategoryTitlePlanCell).concat(subCategoryPlanCells)
            .concat(industryGoalCells)
          }
        )(cpdCategorys)
      }
    </div>
  </div>
}

const toggleCpdCategorys = (trainingPlan, cpdCategorys, industry) => {
  if (fp.isEmpty(cpdCategorys)) {
    return trainingPlan
  } else {
    const cpdCategory = fp.head(cpdCategorys)
    const cpdCategorySelected = fp.find(
      ({ cpdCategoryId }) => cpdCategoryId === cpdCategory.id
    )(trainingPlan)
    const updatedTrainingPlan = cpdCategorySelected ?
      fp.filter(
        ({ cpdCategoryId }) => cpdCategoryId !== cpdCategory.id
      )(trainingPlan) :
      trainingPlan.concat([{
        industryId: industry.id,
        cpdCategoryId: cpdCategory.id,
        maxMicroCpdPortion: industry.maxMicroCpdPortion,
        totalCpdPoints: 0
      }])
    return toggleCpdCategorys(updatedTrainingPlan, fp.tail(cpdCategorys), industry)
  }
}

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withProps(
    ({ industry, industriesWithGoals }) => ({
      industryGoals: fp.flow(
        fp.filter(
          (industryWithGoals) => industryWithGoals.industry.id === industry.id
        ),
        fp.flatMap('goals')
      )(industriesWithGoals)
    })
  ),
  withHandlers({
    onSelectCpdCategory: ({ setTrainingPlan, entity, trainingPlan, industry }) => (cpdCategory) => {
      const updatedTrainingPlan = toggleCpdCategorys(trainingPlan, [cpdCategory], industry)
      setTrainingPlan(entity.id, updatedTrainingPlan)
    },
    batchToggleCpdCategorys: ({ setTrainingPlan, entity, trainingPlan, industry }) => (cpdCategorys) => {
      const updatedTrainingPlan = toggleCpdCategorys(trainingPlan, cpdCategorys, industry)
      setTrainingPlan(entity.id, updatedTrainingPlan)
    },
    addCpdPoints: ({ setTrainingPlan, entity, trainingPlan, industry }) => (cpdCategory) => {
      const cpdCategorySelected = fp.find(
        ({ cpdCategoryId }) => cpdCategoryId === cpdCategory.id
      )(trainingPlan)
      const updatedTrainingPlan = cpdCategorySelected 
        ? fp.map(
            (trainingPlanEntry) => trainingPlanEntry.cpdCategoryId === cpdCategory.id ? {
              ...trainingPlanEntry,
              totalCpdPoints: trainingPlanEntry.totalCpdPoints + 0.5
            } : trainingPlanEntry
          )(trainingPlan)
        : toggleCpdCategorys(trainingPlan, cpdCategorySelected ? [] : [cpdCategory], industry)

      setTrainingPlan(entity.id, updatedTrainingPlan)
    },
    minusCpdPoints: ({ setTrainingPlan, entity, trainingPlan }) => (cpdCategory) => {
      const pointUpdatedTrainingPlan = fp.map(
        (trainingPlanEntry) => trainingPlanEntry.cpdCategoryId === cpdCategory.id ? {
          ...trainingPlanEntry,
          totalCpdPoints: trainingPlanEntry.totalCpdPoints - 0.5
        } : trainingPlanEntry
      )(trainingPlan)
      const filteredTrainingPlan = fp.filter(
        ({ cpdCategoryId, totalCpdPoints }) => !((cpdCategoryId === cpdCategory.id) && (!cpdCategory.mandatory) && (totalCpdPoints < 0))
      )(pointUpdatedTrainingPlan)
      setTrainingPlan(entity.id, filteredTrainingPlan)
    },
    addProgressGoalPoints: ({ setProgressGoal, entity, goals }) => (goalId) => {
      const points = fp.flow(
        fp.find(
          (goal) => goalId === goal.goalId
        ),
        fp.getOr(0)('points')
      )(goals) + 1
      const updatedGoals = fp.filter(
        (goal) => goal.goalId !== goalId
      )(goals).concat([{
        goalId,
        points
      }])
      setProgressGoal(entity.id, updatedGoals)
    },
    minusProgressGoalPoints: ({ setProgressGoal, entity, goals }) => (goalId) => {
      const points = fp.flow(
        fp.find(
          (goal) => goalId === goal.goalId
        ),
        fp.getOr(0)('points')
      )(goals) - 1
      const updatedGoals = fp.filter(
        (goal) => goal.goalId !== goalId
      )(goals).concat(
        points > 0 ?
          [{
            goalId,
            points
          }] :
          []
      )
      setProgressGoal(entity.id, updatedGoals)
    }
  }),
  withHandlers({
    updateCpdDate: ({ entity, industryEntity, setIndustryEntity }) => (e) => {
      const updatedIndustryEntity = {
        ...industryEntity,
        cpdDay: parseInt(e.target.value, 10)
      }
      setIndustryEntity(entity, updatedIndustryEntity)
    },
    updateCpdMonth: ({ entity, industryEntity, setIndustryEntity }) => (e) => {
      const newMonth = e.target.value
      const dateUpperLimit = dateFnsCompat.getDaysInMonth(newMonth)
      const updatedIndustryEntity = {
        ...industryEntity,
        cpdMonth: parseInt(newMonth, 10),
        cpdDay: dateUpperLimit < industryEntity.cpdDay ? 1 : industryEntity.cpdDay
      }
      setIndustryEntity(entity, updatedIndustryEntity)
    },
    minusCpdRequirement: ({ entity, industryEntity, setIndustryEntity }) => () => {
      const updatedIndustryEntity = {
        ...industryEntity,
        cpdPoints: industryEntity.cpdPoints - 1,
      }
      setIndustryEntity(entity, updatedIndustryEntity)
    },
    addCpdRequirement: ({ entity, industryEntity, setIndustryEntity }) => () => {
      const updatedIndustryEntity = {
        ...industryEntity,
        cpdPoints: industryEntity.cpdPoints + 1,
      }
      setIndustryEntity(entity, updatedIndustryEntity)
    }
  })
)(EntityTrainingPlan)


// WEBPACK FOOTER //
// ./src/containers/pages/app/account-settings/components/entity-training-plan.js